<script lang="ts">
  import { tick } from "svelte";
  import { minecraftRuntimeAdvice } from "@mars-host/shared";
  import MinecraftRuntimeNotice from "./MinecraftRuntimeNotice.svelte";
  import { get } from "svelte/store";
  import type { CreateServerInput, Game } from "@/api";
  import {
    gameOptions,
    coreOptions,
    SERVER_HOURLY_COST,
    SERVER_SLOT_COST,
    SERVER_START_COST,
  } from "@/config";
  import { feedback, refreshCapacity } from "@/stores/dashboard";
  import CoreVersionSelect from "@/components/CoreVersionSelect.svelte";
  import Select from "@/components/Select.svelte";
  import Button from "@/components/Button.svelte";
  import FormField from "@/components/FormField.svelte";
  import TextInput from "@/components/TextInput.svelte";

  interface Props {
    serverCount: number;
    serverLimit: number;
    balance: number;
    nodeCapacityAvailable: boolean | null;
    submitting: boolean;
    slotBuying: boolean;
    onSubmit: (input: CreateServerInput) => Promise<boolean>;
    onPurchaseSlot: () => Promise<boolean>;
  }
  const {
    serverCount,
    serverLimit,
    balance,
    nodeCapacityAvailable,
    submitting,
    slotBuying,
    onSubmit,
    onPurchaseSlot,
  }: Props = $props();
  let name = $state("");
  let game = $state<Game>("minecraft_java");
  let core = $state("paper");
  let version = $state("");
  const runtimeAdvice = $derived(minecraftRuntimeAdvice(core, version));
  let javaVersion = $derived(runtimeAdvice.javaVersion);
  let step = $state(0);
  let error = $state("");
  let checkingCapacity = $state(false);
  let heading = $state<HTMLHeadingElement>();
  const steps = ["Игра и название", "Сборка и версия", "Проверка и запуск"];
  const selectedGame = $derived(gameOptions.find((option) => option.value === game)!);
  let versionsReady = $state(false);
  const selectedCore = $derived(coreOptions.find((option) => option.value === core)!);
  const limitReached = $derived(serverCount >= serverLimit);
  const capacityUnavailable = $derived(nodeCapacityAvailable === false);
  const busy = $derived(submitting || slotBuying);
  const canAffordStart = $derived(balance >= SERVER_START_COST);

  const submissionDisabled = $derived.by(() => {
    const creationUnavailable = busy || limitReached;
    return creationUnavailable || !canAffordStart;
  });

  async function moveTo(next: number) {
    if (busy) {
      return;
    }
    error = "";
    step = next;
    await tick();
    heading?.focus();
  }
  function next() {
    if (step === 0 && (name.trim().length < 4 || name.trim().length > 16)) {
      error = "Введите название от 4 до 16 символов. Оно будет видно в списке серверов.";
      return;
    }
    if (step === 1 && !versionsReady) {
      error = "Дождитесь загрузки доступных версий ядра.";
      return;
    }
    void moveTo(Math.min(step + 1, 2));
  }
  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (busy) {
      return;
    }
    if (step < 2) {
      next();
      return;
    }
    error = "";
    if (limitReached) {
      return;
    }
    if (!canAffordStart) {
      error = `Для создания нужно ${SERVER_START_COST} кредитов. Не хватает ${SERVER_START_COST - balance}. Получите ежедневный бонус на странице «Обзор».`;
      return;
    }
    if (name.trim().length < 4 || name.trim().length > 16) {
      await moveTo(0);
      next();
      return;
    }
    const created = await onSubmit({
      name: name.trim(),
      game,
      core,
      version,
      javaVersion,
      memLimitMb: 1024,
    });
    if (!created) {
      error =
        get(feedback)?.text ??
        "Не удалось создать сервер. Проверьте подключение и попробуйте снова.";
    }
  }
  async function purchaseSlot() {
    if (busy || balance < SERVER_SLOT_COST) {
      return;
    }
    error = "";
    if (!(await onPurchaseSlot())) {
      error = get(feedback)?.text ?? "Не удалось увеличить лимит серверов.";
    }
  }
  async function checkCapacity() {
    if (checkingCapacity) {
      return;
    }
    checkingCapacity = true;
    error = "";
    try {
      await refreshCapacity();
    } catch {
      error = "Не удалось проверить доступность. Проверьте подключение и попробуйте снова.";
    } finally {
      checkingCapacity = false;
    }
  }
</script>

<section class="panel-card create-panel" aria-labelledby="create-title">
  <header class="panel-heading">
    <div>
      <p class="eyebrow">Шаг {step + 1} из 3</p>
      <h2 id="create-title" bind:this={heading} tabindex="-1">{steps[step]}</h2>
    </div>
  </header>
  <ol class="step-list" aria-label="Шаги создания сервера">
    {#each steps as label, index (label)}
      <li>
        <button
          type="button"
          class:active={step === index}
          aria-current={step === index ? "step" : undefined}
          disabled={busy || index > step}
          onclick={() => void moveTo(index)}><span>{index + 1}</span>{label}</button
        >
      </li>
    {/each}
  </ol>
  {#if capacityUnavailable}
    <div class="notice" role="status">
      <strong>По последней проверке свободных мест нет</strong>
      <p>Доступность может измениться. Размещение будет проверено при создании сервера.</p>
      <Button variant="ghost" disabled={checkingCapacity || busy} onclick={checkCapacity}
        >{checkingCapacity ? "Проверяем…" : "Проверить доступность"}</Button
      >
    </div>
  {/if}
  {#if limitReached}
    <div class="notice" role="status">
      <strong>Достигнут лимит серверов: {serverCount} из {serverLimit}</strong>
      <p>
        Дополнительное место (слот) позволяет создать ещё один сервер. Покупка разовая, работа
        сервера оплачивается отдельно.
      </p>
      <Button disabled={busy || balance < SERVER_SLOT_COST} onclick={purchaseSlot}
        >{slotBuying ? "Покупаем…" : `Добавить место за ${SERVER_SLOT_COST} кредитов`}</Button
      >
      {#if balance < SERVER_SLOT_COST}<p>
          Не хватает {SERVER_SLOT_COST - balance} кредитов. Баланс: {balance}.
        </p>{/if}
    </div>
  {/if}

  <form onsubmit={submit} aria-busy={submitting}>
    {#if step === 0}
      <FormField
        label="Название сервера"
        hint="От 4 до 16 символов. Это название в панели, его можно изменить позже."
        hintId="name-help"
      >
        <TextInput
          id="server-name"
          bind:value={name}
          minlength={4}
          maxlength={16}
          placeholder="Например, База Марс"
          required
          disabled={busy}
          aria-describedby="name-help"
        />
      </FormField>
      <label for="server-game">Игра</label>
      <Select id="server-game" bind:value={game} options={gameOptions} disabled={busy} />
      <p class="help">
        Для подключения используйте {selectedGame.label}. После создания сервера изменить игру
        нельзя.
      </p>
    {:else if step === 1}
      <fieldset disabled={busy}>
        <legend>Сборка сервера (ядро)</legend>
        <p class="help">Сборка определяет, сможете ли вы установить плагины или моды.</p>
        <div class="core-grid">
          {#each coreOptions.filter( (option) => selectedGame.cores.includes(option.value) ) as option (option.value)}
            <label class="core-choice" class:chosen={core === option.value}>
              <input type="radio" bind:group={core} value={option.value} />
              <span><strong>{option.label}</strong><small>{option.description}</small></span>
            </label>
          {/each}
        </div>
      </fieldset>
      <label for="server-version">Версия Minecraft</label>
      <CoreVersionSelect
        id="server-version"
        {core}
        bind:value={version}
        bind:ready={versionsReady}
        disabled={busy}
      />
      <p class="help">
        Выберите версию, с которой будут подключаться игроки и работать выбранные моды или плагины.
      </p>
      <MinecraftRuntimeNotice {core} {version} />
      <details class="advanced">
        <summary>Дополнительная настройка: версия Java</summary>
        <label for="server-java">Java для запуска сервера</label>
        <Select
          id="server-java"
          bind:value={javaVersion}
          options={[8, 11, 17, 21, 25].map((value) => ({ value, label: `Java ${value}` }))}
          disabled={busy}
        />
        <p class="help">
          Java (JRE) — среда, в которой работает сервер. Меняйте её, если выбранная сборка требует
          другую версию.
        </p>
      </details>
      <p class="warning">
        После создания смена сборки или версии Minecraft удалит файлы сервера, включая мир. Выберите
        нужные параметры сейчас.
      </p>
    {:else}
      <dl class="review">
        <div>
          <dt>Название</dt>
          <dd>{name.trim()}</dd>
        </div>
        <div>
          <dt>Игра</dt>
          <dd>{selectedGame.label}</dd>
        </div>
        <div>
          <dt>Сборка и версия</dt>
          <dd>{selectedCore.label} · {version}</dd>
        </div>
        <div>
          <dt>Среда запуска</dt>
          <dd>Java {javaVersion}</dd>
        </div>

        <div>
          <dt>Оперативная память</dt>
          <dd>1 ГБ</dd>
        </div>
        <div>
          <dt>Лимит серверов</dt>
          <dd>{serverCount + 1} из {serverLimit} после создания</dd>
        </div>
      </dl>
      <MinecraftRuntimeNotice {core} {version} />
      <div class="price-summary">
        <strong>Стоимость в кредитах</strong>
        <p>Кредиты — внутренний баланс для оплаты серверов и улучшений.</p>
        <dl class="review">
          <div>
            <dt>При создании</dt>
            <dd>{SERVER_START_COST} кредитов</dd>
          </div>
          <div>
            <dt>Работа базового сервера</dt>
            <dd>{SERVER_HOURLY_COST} кредитов / час</dd>
          </div>
          <div>
            <dt>Ваш баланс</dt>
            <dd>{balance} кредитов</dd>
          </div>
          {#if canAffordStart}<div>
              <dt>После создания</dt>
              <dd>{balance - SERVER_START_COST} кредитов</dd>
            </div>{/if}
        </dl>
        <p>
          При создании оплачивается первый час. Время ожидания в очереди не расходует оплаченный
          час: он начинается после запуска. При нехватке кредитов работающий сервер автоматически
          остановится.
        </p>
      </div>
      {#if !canAffordStart}<p class="warning">
          Не хватает {SERVER_START_COST - balance} кредитов для создания. Получите ежедневный бонус в
          разделе «Обзор».
        </p>{/if}
      <p class="help">
        После подтверждения сервер установится и запустится автоматически. Этапы установки будут
        видны на его странице.
      </p>
    {/if}
    {#if error}<p class="form-error" role="alert">{error}</p>{/if}
    <div class="form-actions">
      {#if step > 0}<Button variant="ghost" disabled={busy} onclick={() => void moveTo(step - 1)}
          >Назад</Button
        >{/if}
      {#if step < 2}
        <Button type="submit" disabled={busy}>Далее</Button>
      {:else}
        <Button type="submit" disabled={submissionDisabled}
          >{submitting
            ? "Создаём сервер…"
            : `Создать и запустить · ${SERVER_START_COST} кр.`}</Button
        >
      {/if}
    </div>
  </form>
</section>

<style>
  .create-panel {
    overflow: hidden;
  }
  .step-list {
    display: flex;
    gap: 0.75rem;
    margin: 0;
    padding: 1rem 1.5rem;
    list-style: none;
    border-bottom: 1px solid var(--line);
  }
  .step-list li {
    flex: 1;
    min-width: 0;
  }
  .step-list button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0;
    border: 0;
    background: transparent;
    color: var(--muted);
    text-align: left;
    font-size: 0.75rem;
  }
  .step-list span {
    display: grid;
    place-items: center;
    flex: 0 0 1.7rem;
    width: 1.7rem;
    height: 1.7rem;
    border: 1px solid var(--line-strong);
    border-radius: 50%;
  }
  .step-list .active {
    color: var(--mars-light);
  }
  .step-list .active span {
    border-color: var(--mars-light);
  }
  form {
    padding: 1.5rem;
  }
  label,
  legend {
    display: block;
    margin: 0 0 0.5rem;
    color: var(--cream);
    font-size: 0.9rem;
    font-weight: 600;
  }
  input:not([type="radio"]) {
    width: 100%;
    min-height: 3rem;
    padding: 0.65rem 0.8rem;
    border: 1px solid var(--line-strong);
    border-radius: 0.2rem;
    color: var(--cream);
    background: var(--ink);
    font-size: 0.95rem;
  }
  button:focus-visible,
  input:focus-visible,
  summary:focus-visible {
    outline: 2px solid var(--mars-light);
    outline-offset: 3px;
  }
  h2:focus {
    outline: none;
  }
  .help,
  .notice p,
  .price-summary p {
    margin: 0.5rem 0 1.3rem;
    color: var(--muted);
    font-size: 0.82rem;
    line-height: 1.6;
  }
  fieldset {
    min-width: 0;
    border: 0;
    margin: 0 0 1.4rem;
    padding: 0;
  }
  .core-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }
  .core-choice {
    display: flex;
    gap: 0.65rem;
    align-items: flex-start;
    margin: 0;
    padding: 1rem;
    border: 1px solid var(--line-strong);
    cursor: pointer;
  }
  .core-choice.chosen {
    border-color: var(--mars-light);
    background: rgb(214 75 50 / 0.08);
  }
  .core-choice input {
    margin-top: 0.2rem;
    accent-color: var(--mars-light);
  }
  .core-choice span {
    display: grid;
    gap: 0.3rem;
  }
  .core-choice small {
    color: var(--muted);
    font-weight: 400;
    font-size: 0.8rem;
    line-height: 1.5;
  }
  .advanced {
    margin-bottom: 1rem;
    border: 1px solid var(--line);
    padding: 1rem;
  }
  summary {
    cursor: pointer;
    color: var(--muted);
    font-size: 0.82rem;
  }
  .advanced[open] summary {
    margin-bottom: 1rem;
  }
  .advanced .help {
    margin-bottom: 0;
  }
  .warning,
  .notice {
    padding: 1rem;
    border-left: 3px solid var(--amber);
    background: rgb(232 162 77 / 0.07);
    color: var(--cream);
    font-size: 0.82rem;
    line-height: 1.6;
  }
  .notice {
    margin: 1rem 1.5rem;
  }
  .notice p:last-child {
    margin-bottom: 0;
  }
  .review {
    display: grid;
    gap: 0.8rem;
    margin: 0 0 1.5rem;
  }
  .review div {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.85rem;
  }
  dt {
    color: var(--muted);
  }
  dd {
    margin: 0;
    text-align: right;
    overflow-wrap: anywhere;
  }
  .price-summary {
    padding: 1rem;
    border: 1px solid var(--line-strong);
    background: rgb(214 75 50 / 0.05);
  }
  .price-summary p:last-child {
    margin-bottom: 0;
  }
  .form-actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1.5rem;
  }
  @media (max-width: 520px) {
    form {
      padding: 1rem;
    }
    .step-list {
      gap: 0.5rem;
      padding: 1rem;
    }
    .step-list button {
      flex-direction: column;
      align-items: flex-start;
      font-size: 0.7rem;
    }
    .core-grid {
      grid-template-columns: 1fr;
    }
    .notice {
      margin: 1rem;
    }
    .review div {
      flex-direction: column;
      gap: 0.2rem;
    }
    dd {
      text-align: left;
    }
  }
</style>
